import { CalendarCheck, LogIn, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';

const STEPS = [
  {
    icon: CalendarCheck,
    title: 'Browse events',
    description: 'Pick a social session, round robin, or tournament that fits your schedule.',
  },
  {
    icon: LogIn,
    title: 'Sign in & register',
    description: 'Members, students, and guests can register online when spots are available.',
  },
  {
    icon: Trophy,
    title: 'Show up & play',
    description: 'Check in on the day, view your matches, and enjoy organised match play.',
  },
] as const;

interface PublicPlayEventsHowItWorksProps {
  variant?: 'inline' | 'sidebar';
}

export function PublicPlayEventsHowItWorks({ variant = 'inline' }: PublicPlayEventsHowItWorksProps) {
  if (variant === 'sidebar') {
    return (
      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          How it works
        </h3>
        <ol className="space-y-3">
          {STEPS.map((step) => (
            <li key={step.title} className="flex gap-2 text-sm">
              <step.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium text-foreground">{step.title}</p>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <section aria-labelledby="play-events-how-heading" className="space-y-4">
      <h2 id="play-events-how-heading" className="text-lg font-semibold text-foreground">
        How it works
      </h2>
      <ol className="grid gap-3 sm:grid-cols-3">
        {STEPS.map((step, index) => (
          <li key={step.title}>
            <Card className="h-full border-border/80 bg-card/80 shadow-sm">
              <CardContent className="flex h-full gap-3 p-4">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                  aria-hidden
                >
                  {index + 1}
                </div>
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <step.icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <h3 className="text-sm font-semibold text-foreground">{step.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </CardContent>
            </Card>
          </li>
        ))}
      </ol>
    </section>
  );
}
